import { ComponentProps } from "react"

import { cn } from "@/lib/utils"

export interface BaseSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
  children: React.ReactNode
  ref?: React.Ref<HTMLDivElement>
}

export function Root({ className, children, ref, ...props }: BaseSectionProps) {
  return (
    <div
      ref={ref}
      className={cn("rounded-md border border-border bg-white", className)}
      {...props}
    >
      {children}
    </div>
  )
}

interface HeaderSectionProps {
  bg?: "white" | "haze"
  hideBorder?: boolean
}

export function Header({
  className,
  children,
  bg = "white",
  hideBorder = false,
  ref,
  ...props
}: BaseSectionProps & HeaderSectionProps) {
  return (
    <div
      ref={ref}
      className={cn(
        "@container/section-header grid grid-cols-[1fr_auto] grid-rows-[auto_auto] items-start justify-between rounded-t-[inherit] px-5 py-6",
        {
          "bg-haze-100": bg === "haze",
          "bg-white": bg === "white",
        },
        { "border-b border-border": !hideBorder },
        { "pb-4": hideBorder },
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export function Title({
  className,
  children,
  ref,
  ...props
}: ComponentProps<"span"> & { ref?: React.Ref<HTMLHeadingElement> }) {
  return (
    <span
      ref={ref}
      className={cn("row-start-1 text-h2 text-foreground", className)}
      {...props}
    >
      {children}
    </span>
  )
}

export function Subtitle({
  className,
  children,
  ref,
  ...props
}: ComponentProps<"span"> & { ref?: React.Ref<HTMLHeadingElement> }) {
  return (
    <span
      ref={ref}
      className={cn("row-start-2 text-h4 text-foreground", className)}
      {...props}
    >
      {children}
    </span>
  )
}

export function Body({ className, children, ref, ...props }: BaseSectionProps) {
  return (
    <div ref={ref} className={cn("p-5", className)} {...props}>
      {children}
    </div>
  )
}

export function Actions({ className, children, ref, ...props }: BaseSectionProps) {
  return (
    <div
      ref={ref}
      className={cn(
        "col-start-1 flex flex-grow flex-wrap justify-end gap-2 pt-2 @md/section-header:col-start-2 @md/section-header:row-span-2 @md/section-header:ml-5 @md/section-header:pt-0",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}
